import { Injectable } from '@angular/core';
import { generalHelpers, LanguageInterface } from '@mzima-client/sdk';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject } from 'rxjs';
import entityLanguages from '../../../assets/locales/entity-languages.json';
import LangJSON from '../../../assets/locales/languages.json';
import { SessionService } from './session.service';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private languages = new BehaviorSubject<LanguageInterface[]>(this.getLanguages());
  public languages$ = this.languages.asObservable();

  private languageKey = `${generalHelpers.CONST.LOCAL_STORAGE_PREFIX}language`;

  private selectedLanguage = new BehaviorSubject<string>('');
  public selectedLanguage$ = this.selectedLanguage.asObservable();
  private isRTL = new BehaviorSubject<boolean>(false);
  public isRTL$ = this.isRTL.asObservable();

  constructor(
    private translate: TranslateService,
    private session: SessionService,
    private cookieService: CookieService,
  ) {
    this.translate.setDefaultLang('en');

    const storedLang = this.initialLanguage;
    const browserLang = this.detectBrowserLanguage();

    const isStoredLangValid =
      storedLang !== 'null' &&
      storedLang !== null &&
      this.languages.value.find((language) => language.code === storedLang);

    // If stored language is 'en' but browser language is different,
    // prefer browser language
    if (isStoredLangValid && !(storedLang === 'en' && browserLang && browserLang !== 'en')) {
      this.setLanguage(storedLang!);
    } else {
      // Use browser language if available, otherwise fallback to 'en'
      const fallbackLang = browserLang || 'en';
      this.setLanguage(fallbackLang);

      // Persist to localStorage if not fallback
      if (fallbackLang !== 'en') {
        localStorage.setItem(this.languageKey, fallbackLang);
        this.setCookies(fallbackLang);
      }
    }
  }

  public get initialLanguage() {
    return localStorage.getItem(this.languageKey)!;
  }

  private set initialLanguage(value: string) {
    localStorage.setItem(this.languageKey, value);
    this.setCookies(value);
  }

  /**
   * Detects the user's preferred language from browser settings.
   * Checks navigator.languages array (ordered by preference) and returns
   * the first supported language code, or null if none match.
   */
  private detectBrowserLanguage(): string | null {
    const browserLanguages = navigator.languages || [navigator.language];

    for (const browserLang of browserLanguages) {
      // Extract base language code (e.g., 'en-US' → 'en')
      const langCode = browserLang.split('-')[0].toLowerCase();

      // Check if we support this language
      if (this.languages.value.find((lang) => lang.code === langCode)) {
        return langCode;
      }
    }

    return null;
  }
  getEntityLanguages() {
    return entityLanguages;
  }

  getLanguages(): LanguageInterface[] {
    if (LangJSON.languages && LangJSON.languages.length > 0) {
      return LangJSON.languages;
    } else {
      return [
        {
          rtl: false,
          pluralequation: 'language.pluralequation',
          code: 'en',
          name: 'English',
          nplurals: 2,
        },
      ];
    }
  }

  private setLanguage(lang: string) {
    this.translate.use(lang);
    this.selectedLanguage.next(lang);
    this.changeDirection(lang);
  }

  public changeLanguage(value: string) {
    this.translate.use(value);
    localStorage.setItem(this.languageKey, value);
    this.setCookies(value);
    this.selectedLanguage.next(value);
    this.changeDirection(value);
  }

  private changeDirection(value: string) {
    this.isRTL.next(!!this.languages.value.find((l) => l.code === value)?.rtl);
  }

  private setCookies(value: string): void {
    this.cookieService.set(
      this.languageKey,
      JSON.stringify(value),
      300,
      undefined,
      '.resistmap.com',
      true,
    );
  }
}
